import { HttpStatusCode } from 'axios';
import { ResponseModel } from '../models/Response';
import { ApiEndpoints } from '../config/constants';
import apiClient from './api/ApiClient';
import {
    RestaurantSubmission,
    CreateRestaurantSubmissionDto,
    RestaurantSubmissionStatus,
} from '../models/DB';

/**
 * Service for managing restaurant client application submissions
 */
class RestaurantSubmissionServiceClass {
    /**
     * Submit a new restaurant application (public endpoint)
     */
    async submitApplication(
        data: CreateRestaurantSubmissionDto
    ): Promise<RestaurantSubmission | null> {
        try {
            const response = await apiClient.postPublic<ResponseModel<RestaurantSubmission>>(
                ApiEndpoints.RestaurantSubmissions.Create,
                data
            );

            const { data: submission, statusCode, errorMessage } = response.data;

            if (statusCode === HttpStatusCode.Created && submission) {
                console.log('✅ [RestaurantSubmissionService] Application submitted:', submission.id);
                return submission;
            }

            console.error('❌ [RestaurantSubmissionService] Submission failed:', errorMessage);
            return null;
        } catch (error) {
            console.error('❌ [RestaurantSubmissionService] Error submitting application:', error);
            return null;
        }
    }

    /**
     * Get all submissions for the current store (authenticated)
     */
    async getAll(): Promise<RestaurantSubmission[]> {
        try {
            const response = await apiClient.get<ResponseModel<RestaurantSubmission[]>>(
                ApiEndpoints.RestaurantSubmissions.GetAll
            );

            const { data, statusCode, errorMessage } = response.data;

            if (statusCode === HttpStatusCode.Ok && data) {
                console.log(`✅ [RestaurantSubmissionService] Fetched ${data.length} submissions`);
                return data;
            }

            console.error('❌ [RestaurantSubmissionService] Fetch failed:', errorMessage);
            return [];
        } catch (error) {
            console.error('❌ [RestaurantSubmissionService] Error fetching submissions:', error);
            return [];
        }
    }

    /**
     * Get a single submission by ID (authenticated)
     */
    async getById(id: string): Promise<RestaurantSubmission | null> {
        try {
            const response = await apiClient.get<ResponseModel<RestaurantSubmission>>(
                ApiEndpoints.RestaurantSubmissions.GetById(id)
            );

            const { data, statusCode, errorMessage } = response.data;

            if (statusCode === HttpStatusCode.Ok && data) {
                console.log('✅ [RestaurantSubmissionService] Fetched submission:', id);
                return data;
            }

            console.error('❌ [RestaurantSubmissionService] Fetch failed:', errorMessage);
            return null;
        } catch (error) {
            console.error('❌ [RestaurantSubmissionService] Error fetching submission:', error);
            return null;
        }
    }

    /**
     * Update submission status and notes (authenticated)
     */
    async updateStatus(
        id: string,
        status?: RestaurantSubmissionStatus,
        adminNotes?: string
    ): Promise<RestaurantSubmission | null> {
        try {
            const updateData: { status?: RestaurantSubmissionStatus; admin_notes?: string } = {};
            if (status) updateData.status = status;
            if (adminNotes !== undefined) updateData.admin_notes = adminNotes;

            const response = await apiClient.patch<ResponseModel<RestaurantSubmission>>(
                ApiEndpoints.RestaurantSubmissions.Update(id),
                updateData
            );

            const { data, statusCode, errorMessage } = response.data;

            if (statusCode === HttpStatusCode.Ok && data) {
                console.log('✅ [RestaurantSubmissionService] Updated submission:', id);
                return data;
            }

            console.error('❌ [RestaurantSubmissionService] Update failed:', errorMessage);
            return null;
        } catch (error) {
            console.error('❌ [RestaurantSubmissionService] Error updating submission:', error);
            return null;
        }
    }

    /**
     * Delete a submission (authenticated)
     */
    async deleteSubmission(id: string): Promise<boolean> {
        try {
            const response = await apiClient.delete<ResponseModel<null>>(
                ApiEndpoints.RestaurantSubmissions.Delete(id)
            );

            const { statusCode, errorMessage } = response.data;

            if (statusCode === HttpStatusCode.Ok) {
                console.log('✅ [RestaurantSubmissionService] Deleted submission:', id);
                return true;
            }

            console.error('❌ [RestaurantSubmissionService] Delete failed:', errorMessage);
            return false;
        } catch (error) {
            console.error('❌ [RestaurantSubmissionService] Error deleting submission:', error);
            return false;
        }
    }
}

// Export singleton instance
export const RestaurantSubmissionService = new RestaurantSubmissionServiceClass();
export default RestaurantSubmissionService;
